import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { backdropClasses, Typography, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import PopupMenu from '../popupmenu';
import classnames, { DragIndicatorSVG, HexEscape } from './utils';
import Box from '@mui/material/Box';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

const useStyles = makeStyles((theme) => ({
    rowWrapper: {
        display: 'flex',
        alignItems: 'center',
        marginTop:'10px'
    },
    opNode: {
        boxShadow: 'none',
        padding: '0 10px 0 0',
        minWidth: 130,
        borderRadius:'2px',
        backgroundColor: theme.palette.background.opBackground,
        display: 'flex',
        alignItems: 'center',
        '& .rowContents': {
            boxShadow: 'none',
            backgroundColor: theme.styled.filterbuilder.opBackground,
            color: theme.palette.text.primary,
            minWidth: 80,
            padding: '2px 0 2px 0',
            borderStyle: 'none',
            display: 'flex',
            alignItems: 'center',
            borderRadius:'2px',
        },
        '& .rowLabel': {
            padding: 0,
        },
        '& .moveHandle': {
            background: `${theme.styled.filterbuilder.opBackground} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='${HexEscape(theme.styled.filterbuilder.opHandle)}' stroke='transparent' d='${DragIndicatorSVG}' /%3E%3C/svg%3E") no-repeat center;`,
            width: 30,
            marginRight: -5,
            borderStyle: 'none',
            boxShadow: 'none',
        },
        '& .loadingHandle': {
            background: `${theme.styled.filterbuilder.opBackground} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' style='width:24px;height:24px' viewBox='0 0 24 24'%3E%3Cpath fill='${HexEscape(theme.styled.filterbuilder.opHandle)}' stroke='transparent' d='${DragIndicatorSVG}' /%3E%3C/svg%3E") no-repeat center;`,
            width: 30,
            marginRight: -5,
            borderStyle: 'none',
        },
        '& .iconButton': {
            color: theme.palette.background.paper,
        },
    },
    operator: {
        ...theme.typography.button,
        backgroundColor: theme.styled.filterbuilder.opButton,
        borderStyle: 'none',
        borderRadius: 4,
        display: 'inline',
        marginTop: 2,
        marginBottom: 2,
        marginLeft: 2,
        marginRight: 5,
        fontSize: 'small',
        padding: '2px 5px',
        width: 50
    },
    action: {
        color: theme.styled.filterbuilder.opAction,
    },
    popperOverride: {
        width: 'auto',
        display: 'inline',
    },
    align: {
        display: 'inline-block',
        verticalAlign: 'middle'
    },
    rowContents: {
        position: 'relative',
        height: '100%',
        border: 'solid #bbb 1px',
        borderLeft: 'none',
        boxShadow: '0 2px 2px -2px',
        padding: '0 5px 0 10px',
        borderRadius: '2px',
        minWidth: '230px',
        flex: '1 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
    },
    '.rtl.rowContents': {
        borderRight: 'none',
        borderLeft: 'solid #bbb 1px',
        padding: '0 10px 0 5px',
    },
    rowContentsDragDisabled: {
        borderLeft: 'solid #bbb 1px',
    },
    '.rtl.rowContentsDragDisabled': {
        borderRight: 'solid #bbb 1px',
        borderLeft: 'solid #bbb 1px',
    },
    rowLabel: {
        flex: '0 1 auto',
        paddingRight: '20px',
    },
    '.rtl.rowLabel': {
        paddingLeft: '20px',
        paddingRight: 'inherit',
    },
    rowToolbar: {
        flex: '0 1 auto',
        display: 'flex',
    },
    rowTitle: {
        fontWeight: 'bold',
    },
    rowTitleWithSubtitle: {
        fontSize: '85%',
        display: 'block',
        height: '0.8rem',
    },
    rowSubtitle: {
        fontSize: '70%',
        lineHeight: '1',
    },
    operatorIcon: {
        minWidth: '30px',
    },
}));

const NodeOperator = (props) => {

    const {
        isSearchMatch = false,
        isSearchFocus = false,
        node,
        path,
        level,
        style = {},
        connectDragPreview,
        connectDragSource,
        isDragging,
        scaffoldBlockPxWidth,
        toggleChildrenVisibility = null,
        handleAddnode,
        onDelete,
        onChange,
        onToggleExpand,
        draggedNode = null,
        canDrag = false,
        canDrop = false,
        didDrop,
        rowDirection = 'ltr',
        className = '',
        ...otherProps
    } = props;
    const classes = useStyles();
    const theme =useTheme();
    const nodetypes = ['variable', 'group'];
    const operators = ['And', 'Or', 'Not'];
    const rowDirectionClass = rowDirection === 'rtl' ? 'rtl' : null;

    const isDraggedDescendant = draggedNode && false;
    const isLandingPadActive = !didDrop && isDragging;

    return (
        <Box style={{ height: '100%' }} {...otherProps}>
            <Box className={classnames('rowWrapper', rowDirectionClass, classes.rowWrapper)}>
                <Box className={classnames('operatorIcon', classes.operatorIcon)}>
                    {node.children && node.children.length > 0 &&
                        <IconButton onClick={onToggleExpand} size="small" >
                            {node.expanded ? (
                                <RemoveCircleIcon fontSize="small"/>
                            ) : (
                                <AddCircleIcon fontSize="small"/>
                            )}
                        </IconButton>
                    }
                </Box>

                <Box className={classnames(
                    'row',
                    isLandingPadActive && 'rowLandingPad',
                    isLandingPadActive && !canDrop && 'rowCancelPad',
                    isSearchMatch && 'rowSearchMatch',
                    isSearchFocus && 'rowSearchFocus',
                    rowDirectionClass,
                    classes.opNode,
                    className
                )}
                    style={{ opacity: isDraggedDescendant ? 0.5 : 1, ...style }}>
                    <Box
                    className={classnames(
                        'rowContents',
                        !canDrag && 'rowContentsDragDisabled',
                        rowDirectionClass
                    )}>
                    <DragIndicatorIcon fontSize="small"  sx={{ mr: 1, ml: 1, cursor: 'grab' ,color:theme.styled.filterbuilder.opHandle}} />
                        <Box className={classnames('rowLabel', rowDirectionClass)}>
                            <PopupMenu
                                className={classes.operator}
                                popperclass={classes.popperOverride}
                                items={operators}
                                selection={node.operator || 'And'}
                                onSelect={(operator) => onChange(node.id, { operator })}
                                title={node.operator}
                                size='small'
                                placement={'bottom-start'}
                                zIndex={1500} />
                        </Box>
                        <Box className="rowToolbar">
                            {path.length < 6 && <PopupMenu key={'addchildbtn'}
                                className={classes.popperOverride}
                                items={nodetypes}
                                onSelect={(type) => handleAddnode(node.id, type)}
                                icon={<AddBoxIcon className={classes.action} />}
                                variant='outlined'
                                size='small'
                                placement={'bottom-end'}
                                zIndex={1500} />}
                            {!node.root && <IconButton
                                key={'deletenode'}
                                className={classes.action}
                                variant='outlined'
                                aria-label='delete node'
                                onClick={() => onDelete()}
                                size="small">
                                <DeleteIcon variant="primary" />
                            </IconButton>}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

NodeOperator.propTypes = {
    node: PropTypes.shape({
        id: PropTypes.string.isRequired,
        operator: PropTypes.oneOfType([PropTypes.func, PropTypes.string]), // default 'And'
        expanded: PropTypes.bool,
        root: PropTypes.bool,
        children: PropTypes.array,
    }).isRequired,
    path: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    handleAddnode: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    canDrag: PropTypes.bool,
    canDrop: PropTypes.bool,
    rowDirection: PropTypes.oneOf(['ltr', 'rtl']),
};

export default NodeOperator;